import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, FadeUp, ChapterLabel } from "@/components/Reveal";
import { services as servicesFallback } from "@/data/services";
import { useSiteData, defaultSettings } from "@/api";

const SERVICE_IMG = "/chantiers/services.jpg";

const Services = () => {
  const site = useSiteData({ chantiers: null, services: servicesFallback, settings: defaultSettings });
  const listeServices = site.services || servicesFallback;
  const active = listeServices.filter((s) => s.active);
  const upcoming = listeServices.filter((s) => !s.active);

  return (
    <>
      <Seo
        title="Nos services — Peinture intérieure décorative à Angoulême & Cognac | Alexis Peinture"
        description="Peinture intérieure décorative dans le Grand Angoulême et le Grand Cognac : préparation minutieuse, teintes conseillées, finitions soignées. Devis gratuit."
      />

      <section className="px-6 pb-20 pt-40 lg:px-12 lg:pt-48">
        <div className="mx-auto max-w-7xl">
          <ChapterLabel number="01" label="Services" />
          <h1
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            data-testid="services-title"
          >
            <MaskedLines
              lines={[
                "Des murs qui ont",
                <span key="l2">
                  du <em className="italic text-clay">caractère.</em>
                </span>,
              ]}
            />
          </h1>
        </div>
      </section>

      {active.map((s) => (
        <section key={s.id} className="mx-auto max-w-7xl px-6 pb-28 lg:px-12" data-testid={`service-detail-${s.id}`}>
          <div className="grid items-start gap-16 lg:grid-cols-5">
            <FadeUp className="lg:col-span-2 lg:sticky lg:top-32">
              <div className="overflow-hidden">
                <img
                  src={SERVICE_IMG}
                  alt="Façade de maison avec volets repeints en bleu pastel"
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover transition-transform duration-[2000ms] ease-out hover:scale-[1.04]"
                />
              </div>
            </FadeUp>
            <div className="lg:col-span-3">
              <FadeUp delay={0.1}>
                <p className="text-xs uppercase tracking-[0.25em] text-clay">Notre spécialité</p>
                <h2 className="mt-4 font-serif text-4xl tracking-tight sm:text-5xl">{s.titre}</h2>
                <p className="mt-8 max-w-xl text-base leading-relaxed text-bark md:text-lg">
                  {s.description}
                </p>
              </FadeUp>
              {(s.points || []).length > 0 && (
                <div className="mt-14 space-y-8">
                  {s.points.map((p, i) => (
                    <FadeUp key={p} delay={0.15 + i * 0.08}>
                      <div className="flex items-start gap-5 border-b border-ink/10 pb-8">
                        <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-clay/10 text-clay">
                          <Check size={14} />
                        </span>
                        <div>
                          <p className="text-xs uppercase tracking-[0.25em] text-taupe">
                            Étape {String(i + 1).padStart(2, "0")}
                          </p>
                          <p className="mt-2 leading-relaxed text-ink">{p}</p>
                        </div>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              )}
              <FadeUp delay={0.2} className="mt-12">
                <Link
                  to="/contact"
                  data-testid="services-cta-devis"
                  className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm font-medium text-cream transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-ink"
                >
                  Demander un devis pour cette prestation
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeUp>
            </div>
          </div>
        </section>
      ))}

      {upcoming.length > 0 && (
        <section className="bg-plaster/60 py-24 lg:py-32" data-testid="services-upcoming">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <ChapterLabel number="02" label="Prochainement" />
            <FadeUp delay={0.1}>
              <h2 className="mt-8 max-w-2xl font-serif text-3xl leading-snug tracking-tight sm:text-4xl">
                L'atelier grandit — d'autres prestations arrivent <em className="italic text-clay">bientôt.</em>
              </h2>
            </FadeUp>
            <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {upcoming.map((s, i) => (
                <FadeUp key={s.id} delay={i * 0.08}>
                  <div className="h-full bg-cream p-8" data-testid={`upcoming-service-${s.id}`}>
                    <p className="text-xs uppercase tracking-[0.25em] text-taupe">Bientôt</p>
                    <h3 className="mt-4 font-serif text-xl tracking-tight">{s.titre}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-bark">{s.resume}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <p className="mt-12 max-w-xl text-sm leading-relaxed text-bark">
                Un besoin qui ne figure pas encore ici ? Parlez-en à Alexis — il
                vous dira franchement s'il peut le prendre en charge.
              </p>
              <Link
                to="/contact"
                data-testid="services-upcoming-cta"
                className="mt-6 inline-flex items-center gap-2 border-b border-ink pb-1 text-sm transition-colors duration-300 hover:border-clay hover:text-clay"
              >
                Poser ma question <ArrowRight size={15} />
              </Link>
            </FadeUp>
          </div>
        </section>
      )}
    </>
  );
};

export default Services;
